import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Upload } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchBookById, updateBook, clearCurrentBook } from '../../store/books/booksSlice';
import { Button, Input, LoadingSpinner } from '../../components/common';
import { FileType } from '../../types';

const bookSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  author: z.string().min(1, 'Author is required'),
  isbn: z.string().min(10, 'ISBN must be at least 10 characters'),
  description: z.string().optional(),
  category: z.string().optional(),
  publishedYear: z.string().optional(),
  pageCount: z.string().optional(),
  fileType: z.nativeEnum(FileType).optional(),
  downloadable: z.boolean().optional(),
});

type BookFormData = z.infer<typeof bookSchema>;

export const EditBookPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentBook, isLoading, error } = useAppSelector((state) => state.books);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [bookFile, setBookFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BookFormData>({
    resolver: zodResolver(bookSchema),
  });

  useEffect(() => {
    if (id) {
      dispatch(fetchBookById(id));
    }
    return () => {
      dispatch(clearCurrentBook());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (currentBook) {
      reset({
        title: currentBook.title,
        author: currentBook.author,
        isbn: currentBook.isbn,
        description: currentBook.description || '',
        category: currentBook.category || '',
        publishedYear: currentBook.publishedYear ? String(currentBook.publishedYear) : '',
        pageCount: currentBook.pageCount ? String(currentBook.pageCount) : '',
        fileType: currentBook.fileType || FileType.PDF,
        downloadable: currentBook.downloadable ?? true,
      });
      if (currentBook.coverImage) {
        setCoverPreview(currentBook.coverImage);
      }
    }
  }, [currentBook, reset]);

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBookFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBookFile(file);
    }
  };

  const onSubmit = async (data: BookFormData) => {
    if (!id) return;

    const result = await dispatch(
      updateBook({
        id,
        data: {
          title: data.title,
          author: data.author,
          isbn: data.isbn,
          description: data.description || undefined,
          category: data.category || undefined,
          publishedYear: data.publishedYear ? parseInt(data.publishedYear, 10) : undefined,
          pageCount: data.pageCount ? parseInt(data.pageCount, 10) : undefined,
          fileType: data.fileType,
          downloadable: data.downloadable ?? true,
        },
        coverImage: coverImage || undefined,
        bookFile: bookFile || undefined,
      })
    );

    if (updateBook.fulfilled.match(result)) {
      navigate('/admin/books');
    }
  };

  if (isLoading && !currentBook) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <button
        className="flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base text-slate-600 hover:text-slate-900 mb-4 sm:mb-6 transition-colors"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
        Back to Books
      </button>

      <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6">
        <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 mb-4 sm:mb-6">Edit Book</h1>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="flex justify-center md:block">
              <label className="block cursor-pointer w-full max-w-[200px] md:max-w-none">
                <div className="aspect-[3/4] bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 hover:border-blue-500 transition-colors overflow-hidden">
                  {coverPreview ? (
                    <img src={coverPreview} alt="Cover preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                      <Upload size={24} className="sm:w-8 sm:h-8" />
                      <span className="mt-2 text-xs sm:text-sm">Upload Cover Image</span>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  name="coverImage"
                  accept="image/*"
                  onChange={handleCoverChange}
                  className="hidden"
                />
              </label>
            </div>

            <div className="md:col-span-2 space-y-3 sm:space-y-4">
              <Input
                id="title"
                label="Title *"
                placeholder="Enter book title"
                error={errors.title?.message}
                {...register('title')}
              />

              <Input
                id="author"
                label="Author *"
                placeholder="Enter author name"
                error={errors.author?.message}
                {...register('author')}
              />

              <Input
                id="isbn"
                label="ISBN *"
                placeholder="Enter ISBN"
                error={errors.isbn?.message}
                {...register('isbn')}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="mb-0">
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Category</label>
                  <select
                    {...register('category')}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select category</option>
                    <option value="Christian book">Cahristian book</option>
                    <option value="CDF Manual">CDF Manual</option>
                    <option value="Educational books">Educational books</option>
                  </select>
                </div>

                <Input
                  id="publishedYear"
                  type="number"
                  label="Published Year"
                  placeholder="e.g., 2024"
                  {...register('publishedYear')}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <Input
                  id="pageCount"
                  type="number"
                  label="Page Count"
                  placeholder="Number of pages"
                  {...register('pageCount')}
                />

                <div className="mb-0">
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">File Type</label>
                  <select
                    {...register('fileType')}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={FileType.PDF}>PDF</option>
                    <option value={FileType.EPUB}>EPUB</option>
                  </select>
                </div>
              </div>

              <div className="mb-0">
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  {...register('description')}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter book description"
                  rows={3}
                />
              </div>

              <div className="mb-0">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    {...register('downloadable')}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-xs sm:text-sm font-medium text-slate-700">Allow consumers to download this book</span>
                </label>
              </div>

              <div className="mb-0">
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Book File</label>
                <label className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                  <Upload size={18} className="text-slate-500 sm:w-5 sm:h-5" />
                  <span className="text-xs sm:text-sm text-slate-600 truncate">{bookFile ? bookFile.name : 'Choose new file (PDF/EPUB)'}</span>
                  <input
                    type="file"
                    name="bookFile"
                    accept=".pdf,.epub"
                    onChange={handleBookFileChange}
                    className="hidden"
                  />
                </label>
                {currentBook?.fileUrl && !bookFile && (
                  <span className="text-xs sm:text-sm text-slate-500 mt-1 block">Current file: {currentBook.fileType.toUpperCase()}</span>
                )}
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-2 sm:pt-4">
                <Button type="button" variant="secondary" onClick={() => navigate(-1)} className="w-full sm:w-auto">
                  Cancel
                </Button>
                <Button type="submit" isLoading={isLoading} className="w-full sm:w-auto">
                  Update Book
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
